/**
 * ConnectorsPage — the integration connector landscape (INT-002/003/004/006,
 * FND-009).
 *
 * A status surface for the single integration engine's connectors: SFTP census,
 * OneSource eligibility, QuickBooks, RingCentral, Twilio, M365, FHIR, and Epic.
 * Each row shows live/configured/planned status, direction, last run, and a
 * note. Complements the operational IntegrationPage (job log + dead letters);
 * this is the "what is wired" view.
 *
 * Data via useConnectors(); degrades gracefully to a seed of the full connector
 * set on 404.
 */
import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useConnectors, type ConnectorStatus } from '../hooks/useConnectors';
import { colors, radius, space } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { useShellSlot } from '../components/shellContext';

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function statusTier(status: ConnectorStatus['status']): 'success' | 'info' | 'neutral' {
  if (status === 'live') return 'success';
  if (status === 'configured') return 'info';
  return 'neutral';
}

export function ConnectorsPage() {
  useShellSlot({ breadcrumb: 'Connectors' });
  const { connectors, available, isLoading, error, refresh } = useConnectors();

  const counts = connectors.reduce(
    (acc, c) => {
      acc[c.status] += 1;
      return acc;
    },
    { live: 0, configured: 0, planned: 0 } as Record<ConnectorStatus['status'], number>
  );

  const columns: Column<ConnectorStatus>[] = [
    { key: 'label', header: 'Connector', render: (c) => <span style={{ fontWeight: 600 }}>{c.label}</span> },
    { key: 'kind', header: 'Type', render: (c) => <span style={{ ...muted, textTransform: 'capitalize' }}>{c.kind}</span> },
    { key: 'status', header: 'Status', render: (c) => <StatusBadge status={statusTier(c.status)} label={c.status} /> },
    { key: 'last_run', header: 'Last run', render: (c) => <span style={muted}>{fmtDateTime(c.last_run)}</span> },
    { key: 'notes', header: 'Notes', render: (c) => <span style={muted}>{c.notes ?? '—'}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Connectors"
        subtitle="The single integration engine's connector landscape — every per-connector adapter and its status (FND-009, INT-002/003/004/006). Operational job log and dead letters live under Integration."
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/integration" className="btn btn-secondary btn-sm">
              Job log →
            </Link>
            <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
              Refresh
            </button>
          </div>
        }
      />

      {!available && (
        <p style={seedNotice} role="status">
          Showing the configured connector set. Live status appears automatically once the connector
          catalogue endpoint is online.
        </p>
      )}
      {error && (
        <p style={errorText} role="alert">
          {error.message}
        </p>
      )}

      <div style={countStrip}>
        <Count label="Live" value={counts.live} color={colors.success} />
        <Count label="Configured" value={counts.configured} color={colors.info} />
        <Count label="Planned" value={counts.planned} color={colors.textSecondary} />
      </div>

      <Card title="Connector catalogue" padded={false}>
        {isLoading && !connectors.length ? (
          <div style={{ padding: 24 }}>
            <LoadingIndicator text="Loading connectors…" />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={connectors}
            rowKey={(c) => c.name}
            emptyTitle="No connectors configured"
          />
        )}
      </Card>
    </>
  );
}

function Count({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={countCard}>
      <div style={{ ...countValue, color }}>{value}</div>
      <div style={countLabel}>{label}</div>
    </div>
  );
}

const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const seedNotice: CSSProperties = {
  margin: '0 0 16px',
  padding: '8px 12px',
  background: colors.brandTint,
  color: colors.brandActive,
  borderRadius: radius.sm,
  fontSize: '0.82rem',
};
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', margin: '0 0 12px' };
const countStrip: CSSProperties = { display: 'flex', gap: 12, marginBottom: space.lg };
const countCard: CSSProperties = {
  flex: '0 0 auto',
  minWidth: 110,
  background: colors.white,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: '12px 18px',
};
const countValue: CSSProperties = { fontSize: '1.6rem', fontWeight: 800, lineHeight: 1 };
const countLabel: CSSProperties = { fontSize: '0.78rem', color: colors.textSecondary, marginTop: 4 };
